import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { AuthProvider } from './auth/AuthProvider.js';
import { ChangePasswordScreen } from './auth/ChangePasswordScreen.js';
import { LoginScreen } from './auth/LoginScreen.js';
import { useAuth } from './auth/useAuth.js';
import { AppShell } from './shell/AppShell.js';
import { ErrorBoundary } from './shell/ErrorBoundary.js';
import { AuditScreen } from './audit/AuditScreen.js';
import { DomainScreen } from './domains/DomainScreen.js';
import { GroupScreen } from './groups/GroupScreen.js';
import { GroupsScreen } from './groups/GroupsScreen.js';
import { NavigationGuardProvider } from './navigation/GuardContext.js';
import { LivenessScreen } from './liveness/LivenessScreen.js';
import { ResolveScreen } from './resolve/ResolveScreen.js';
import { SettingsScreen } from './settings/SettingsScreen.js';
import { UrlScreen } from './urls/UrlScreen.js';
import { UrlsScreen } from './urls/UrlsScreen.js';
import { AcceptInviteScreen } from './users/AcceptInviteScreen.js';
import { UsersScreen } from './users/UsersScreen.js';

/**
 * Lazy, deliberately: Recharts measures 112 KB gzipped on its own — about 37%
 * of the whole 300 KB JS budget — for this one screen. Loading it on demand
 * keeps it out of the entry chunk that every other screen pays for.
 */
const ReportScreen = lazy(() =>
  import('./reports/ReportScreen.js').then((module) => ({ default: module.ReportScreen })),
);

function ScreenFallback() {
  return (
    <p role="status" className="text-ink-subtle">
      Loading…
    </p>
  );
}

function Gate() {
  const { status, user, signIn, changePassword } = useAuth();
  const invited = useLocation().pathname.startsWith('/invite/');

  if (status === 'loading') {
    return (
      <main className="grid min-h-screen place-items-center">
        <p role="status" className="text-ink-subtle">
          Loading…
        </p>
      </main>
    );
  }
  /*
   * The invitation link resolves before the sign-in gate, deliberately.
   *
   * Its visitor HAS no account to sign in to — that is the whole point — so
   * gating it would show them a login form they cannot use and swallow the
   * only credential they have. It is rendered here rather than inside the
   * shell for the same reason.
   */
  if (invited) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<AcceptInviteScreen />} />
      </Routes>
    );
  }

  if (status === 'anonymous' || user === null) return <LoginScreen signIn={signIn} />;
  if (user.mustChangePassword) {
    return <ChangePasswordScreen changePassword={changePassword} forced />;
  }

  /* The guard provider wraps the routed shell, not each screen: the exits it
     protects — the header nav and the ⌘K palette — live in the shell, and a
     screen holding an unsaved draft registers its veto from underneath. */
  return (
    <NavigationGuardProvider>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<Navigate to="/groups" replace />} />
          <Route path="/groups" element={<GroupsScreen />} />
          <Route path="/groups/:groupId" element={<GroupScreen />} />
          <Route path="/domains/:domainId" element={<DomainScreen />} />
          <Route path="/urls" element={<UrlsScreen />} />
          <Route path="/urls/:entryId" element={<UrlScreen />} />
          <Route path="/users" element={<UsersScreen />} />
          <Route path="/liveness" element={<LivenessScreen />} />
          <Route path="/resolve" element={<ResolveScreen />} />
          <Route
            path="/reports"
            element={
              <Suspense fallback={<ScreenFallback />}>
                <ReportScreen />
              </Suspense>
            }
          />
          <Route path="/audit" element={<AuditScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="*" element={<Navigate to="/groups" replace />} />
        </Route>
      </Routes>
    </NavigationGuardProvider>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </ErrorBoundary>
  );
}
